import { ALL_CALENDAR_RESPONSE_EVENTS } from 'app/activity/helpers/activity-event-names';
import { isPast } from 'date-fns';
import { ActivityEventNames, CalendarEventResponseStatusEnum } from 'generated/graphql';
import { isNilOrEmpty } from 'helpers';
import { uniq as _uniq, isNil } from 'lodash-es';

export const getResponseLabel = (response: CalendarEventResponseStatusEnum): string => {
  switch (response) {
    case CalendarEventResponseStatusEnum.Rsvp:
      return 'RSVP';
    case CalendarEventResponseStatusEnum.Confirmed:
      return 'Confirm';
    case CalendarEventResponseStatusEnum.Declined:
      return 'Decline';
    case CalendarEventResponseStatusEnum.DoubleConfirmed:
      return 'Double-confirm';
    case CalendarEventResponseStatusEnum.NoShow:
      return 'No-show';
  }
  return response;
};

export const getResponseLabelPastTense = (response: CalendarEventResponseStatusEnum): string => {
  switch (response) {
    case CalendarEventResponseStatusEnum.Rsvp:
      return 'RSVP';
    case CalendarEventResponseStatusEnum.Confirmed:
      return 'Confirmed';
    case CalendarEventResponseStatusEnum.Declined:
      return 'Declined';
    case CalendarEventResponseStatusEnum.DoubleConfirmed:
      return 'Double-confirmed';
    case CalendarEventResponseStatusEnum.NoShow:
      return 'No-show';
  }
  return response;
};

export const getResponseIcon = (response: CalendarEventResponseStatusEnum): string => {
  switch (response) {
    case CalendarEventResponseStatusEnum.Attended:
      return 'fas fa-user-circle';
    case CalendarEventResponseStatusEnum.Confirmed:
      return 'fa-check';
    case CalendarEventResponseStatusEnum.Declined:
      return 'fa-circle-xmark';
    case CalendarEventResponseStatusEnum.DoubleConfirmed:
      return 'fa-check-double';
    case CalendarEventResponseStatusEnum.NoShow:
      return 'fa-user-slash';
    case CalendarEventResponseStatusEnum.Rsvp:
      return 'fa-circle-check';
    case CalendarEventResponseStatusEnum.Tentative:
      return 'fa-question-square';
  }
};

export const getResponseIconWithColor = (response: CalendarEventResponseStatusEnum): string => {
  const icon = getResponseIcon(response);
  let colorClass = '';
  switch (response) {
    case CalendarEventResponseStatusEnum.Attended:
    case CalendarEventResponseStatusEnum.Confirmed:
    case CalendarEventResponseStatusEnum.DoubleConfirmed:
    case CalendarEventResponseStatusEnum.Rsvp:
      colorClass = ' color--green';
      break;
    case CalendarEventResponseStatusEnum.Declined:
    case CalendarEventResponseStatusEnum.NoShow:
    case CalendarEventResponseStatusEnum.Tentative:
      colorClass = ' color--gray--004';
      break;
  }
  return icon + colorClass;
};

const order = [
  CalendarEventResponseStatusEnum.Rsvp.toString(),
  CalendarEventResponseStatusEnum.Confirmed.toString(),
  CalendarEventResponseStatusEnum.DoubleConfirmed.toString(),
  CalendarEventResponseStatusEnum.Tentative.toString(),
  CalendarEventResponseStatusEnum.Declined.toString(),
  CalendarEventResponseStatusEnum.Attended.toString(),
  CalendarEventResponseStatusEnum.NoShow.toString(),
];

export const getNextCalendarEventResponseStatus = (
  currentStatus: CalendarEventResponseStatusEnum | null | undefined,
  eventHasStarted: boolean
): CalendarEventResponseStatusEnum => {
  if (currentStatus === CalendarEventResponseStatusEnum.Declined ||
    currentStatus === CalendarEventResponseStatusEnum.NoShow ||
    currentStatus === CalendarEventResponseStatusEnum.Attended) {
    return currentStatus;
  }
  if (eventHasStarted) {
    return CalendarEventResponseStatusEnum.Attended;
  }
  if (!currentStatus) {
    return CalendarEventResponseStatusEnum.Rsvp;
  }
  switch (currentStatus) {
    case CalendarEventResponseStatusEnum.Rsvp:
      return CalendarEventResponseStatusEnum.Confirmed;
    case CalendarEventResponseStatusEnum.Confirmed:
      return CalendarEventResponseStatusEnum.DoubleConfirmed;
    case CalendarEventResponseStatusEnum.DoubleConfirmed:
      return CalendarEventResponseStatusEnum.DoubleConfirmed;
    case CalendarEventResponseStatusEnum.Tentative:
    default:
      return CalendarEventResponseStatusEnum.Rsvp;
  }
};

export const orderedResponseOptions = Object.keys(CalendarEventResponseStatusEnum)
  .sort((a, b) => order.indexOf(CalendarEventResponseStatusEnum[a]) - order.indexOf(CalendarEventResponseStatusEnum[b]))
  .map((key) => CalendarEventResponseStatusEnum[key]);

export const attendingResponses = orderedResponseOptions.filter(op =>
  op !== CalendarEventResponseStatusEnum.Declined &&
  op !== CalendarEventResponseStatusEnum.Tentative &&
  op !== CalendarEventResponseStatusEnum.NoShow);

export const notAttendingResponses = orderedResponseOptions.filter(op =>
  op !== CalendarEventResponseStatusEnum.Declined &&
  op === CalendarEventResponseStatusEnum.Tentative ||
  op === CalendarEventResponseStatusEnum.NoShow);

export const responseToCountKey = (response: CalendarEventResponseStatusEnum): string => {
  switch (response) {
    case CalendarEventResponseStatusEnum.Rsvp:
      return 'rsvp';
    case CalendarEventResponseStatusEnum.Confirmed:
      return 'confirmed';
    case CalendarEventResponseStatusEnum.DoubleConfirmed:
      return 'doubleConfirmed';
    case CalendarEventResponseStatusEnum.Tentative:
      return 'tentative';
    case CalendarEventResponseStatusEnum.Declined:
      return 'declined';
    case CalendarEventResponseStatusEnum.Attended:
      return 'attended';
    case CalendarEventResponseStatusEnum.NoShow:
      return 'noShow';
  }
};

export const getCountLabel = (count, response: CalendarEventResponseStatusEnum): string => {
  switch (response) {
    case CalendarEventResponseStatusEnum.Rsvp:
      return `${count} RSVP${count !== 1 ? 's' : ''}`;
    case CalendarEventResponseStatusEnum.Confirmed:
      return `${count} confirmed`;
    case CalendarEventResponseStatusEnum.DoubleConfirmed:
      return `${count} double-confirmed`;
    case CalendarEventResponseStatusEnum.Tentative:
      return `${count} tentative`;
    case CalendarEventResponseStatusEnum.Declined:
      return `${count} declined`;
    case CalendarEventResponseStatusEnum.NoShow:
      return `${count} no-show${count !== 1 ? 's' : ''}`;
    case CalendarEventResponseStatusEnum.Attended:
      return `${count} attended`;
  };
};

export const allowResponseOption = (
  currentResponse: CalendarEventResponseStatusEnum,
  responseOption: CalendarEventResponseStatusEnum,
  eventHasStarted: boolean): boolean => {
  // Don't include current response option - it's displayed separately at the top of the list
  if (responseOption === currentResponse) {
    return false;
  }
  switch (responseOption) {
    // Event hasn't started yet, include "next" response and tentative/decline
    case CalendarEventResponseStatusEnum.Rsvp:
      return !eventHasStarted && (!currentResponse ||
        // If declined or tentative, allow changing to RSVP
        currentResponse === CalendarEventResponseStatusEnum.Declined ||
        currentResponse === CalendarEventResponseStatusEnum.Tentative);
    case CalendarEventResponseStatusEnum.Confirmed:
      return !eventHasStarted && currentResponse === CalendarEventResponseStatusEnum.Rsvp;
    case CalendarEventResponseStatusEnum.DoubleConfirmed:
      return !eventHasStarted && currentResponse === CalendarEventResponseStatusEnum.Confirmed;
    case CalendarEventResponseStatusEnum.Tentative:
    case CalendarEventResponseStatusEnum.Declined:
      return !eventHasStarted;

    // Event has started, include attended/no-show
    case CalendarEventResponseStatusEnum.Attended:
    case CalendarEventResponseStatusEnum.NoShow:
      return eventHasStarted;
  }
};

export const getTotalAttendingEvent = (eventInfo) =>
  attendingResponses.reduce((a, b) => a +
    getEventResponseCount(eventInfo, b), 0);

export const getTotalNotAttendingEvent = (eventInfo) =>
  notAttendingResponses.reduce((a, b) => a +
    getEventResponseCount(eventInfo, b), 0);

export const getEventResponseCount = (eventInfo, response: CalendarEventResponseStatusEnum) =>
  (eventInfo.responseCounts ? eventInfo.responseCounts[responseToCountKey(response)] || 0 : 0);

export const getTotalAttendingLabel = (eventInfo) =>
  `${getTotalAttendingEvent(eventInfo)} ${isPast(new Date(eventInfo.startDate)) ? 'attended' : 'attending'}`;

export const responseStatusToActivityEventName = (rsvpType: CalendarEventResponseStatusEnum | string): ActivityEventNames => {
  switch (rsvpType) {
    case CalendarEventResponseStatusEnum.Attended:
      return ActivityEventNames.UpdateCalendarEventResponseStatusAttended;
    case CalendarEventResponseStatusEnum.Confirmed:
      return ActivityEventNames.UpdateCalendarEventResponseStatusConfirmed;
    case CalendarEventResponseStatusEnum.Declined:
      return ActivityEventNames.UpdateCalendarEventResponseStatusDeclined;
    case CalendarEventResponseStatusEnum.DoubleConfirmed:
      return ActivityEventNames.UpdateCalendarEventResponseStatusDoubleConfirmed;
    case CalendarEventResponseStatusEnum.NoShow:
      return ActivityEventNames.UpdateCalendarEventResponseStatusNoShow;
    case CalendarEventResponseStatusEnum.Rsvp:
      return ActivityEventNames.UpdateCalendarEventResponseStatusRsvp;
    case CalendarEventResponseStatusEnum.Tentative:
      return ActivityEventNames.UpdateCalendarEventResponseStatusTentative;
  }
};

export const activityEventNameToResponseStatus = (activityStreamName: ActivityEventNames): CalendarEventResponseStatusEnum => {
  switch (activityStreamName) {
    case ActivityEventNames.UpdateCalendarEventResponseStatusAttended:
      return CalendarEventResponseStatusEnum.Attended;
    case ActivityEventNames.UpdateCalendarEventResponseStatusConfirmed:
      return CalendarEventResponseStatusEnum.Confirmed;
    case ActivityEventNames.UpdateCalendarEventResponseStatusDeclined:
      return CalendarEventResponseStatusEnum.Declined;
    case ActivityEventNames.UpdateCalendarEventResponseStatusDoubleConfirmed:
      return CalendarEventResponseStatusEnum.DoubleConfirmed;
    case ActivityEventNames.UpdateCalendarEventResponseStatusNoShow:
      return CalendarEventResponseStatusEnum.NoShow;
    case ActivityEventNames.UpdateCalendarEventResponseStatusRsvp:
      return CalendarEventResponseStatusEnum.Rsvp;
    case ActivityEventNames.UpdateCalendarEventResponseStatusTentative:
      return CalendarEventResponseStatusEnum.Tentative;
  }
};

export const getActivityEventNamesForEventFilter = ({ currentNames, calendarEventIds, rsvpStatuses, responseTags }:
  {
    currentNames: string[];
    calendarEventIds: string[];
    rsvpStatuses: CalendarEventResponseStatusEnum[];
    responseTags: { id: string }[];
  }): string[] => {
  const calendarEventNames = [];
  if (!isNilOrEmpty(responseTags)) {
    // Tags are selected
    calendarEventNames.push(ActivityEventNames.CreateCalendarEventResponseTags);
  }
  if (!isNil(rsvpStatuses)) {
    // RSVP statuses are selected
    calendarEventNames.push(...rsvpStatuses.map(r => responseStatusToActivityEventName(r)));
  } else {
    // RSVP statuses are not selected
    if (!isNilOrEmpty(calendarEventIds)) {
      // Calendar events are selected, no RSVP status selected, include all event names
      calendarEventNames.push(...ALL_CALENDAR_RESPONSE_EVENTS);
    }
  }
  return _uniq([
    // Remove all calendar event names
    ...currentNames.filter(r => !ALL_CALENDAR_RESPONSE_EVENTS.find(n => n === r)),
    // Add calendar event names for current filters
    ...calendarEventNames,
  ]);
};
